import { Box, Fab, Stack, Tooltip } from '@mui/material';
import { useState, useEffect } from 'react';
import { Query, useQueryClient } from '@tanstack/react-query';
import { useHistory } from 'react-router-dom';
import theme from 'theme';
import useQueryParams from 'hooks/useQueryParams';
import useCreateTemplate from 'hooks/mutations/useCreateTemplate';
import useUpdateTemplate from 'hooks/mutations/useUpdateTemplate';
import useBeeConfig from 'hooks/queries/useBeeConfig';
import useTemplate from 'hooks/queries/useTemplate';
import useTemplateHTML from 'hooks/queries/useTemplateHTML';
import useRowDisplayConditions from 'hooks/queries/useDisplayConditions';
import { BeeConfigType, URLPaths } from 'models/enums';
import Alert from 'domains/core/components/Alert';
import Button from 'domains/core/components/Button';
import ContentNameInput from 'domains/core/components/ContentNameInput';
import ErrorSnackbar from 'domains/core/components/Snackbars/ErrorSnackbar';
import EmailExportSnackbar from './EmailExportSnackbar';
import { extractDisplayConditions } from 'domains/content/components/Bee/utils/extractDisplayConditions';
import {
    ApplicationAccessType,
    CommunicationType,
    ContentTypeForComparison,
    CreateTemplateInput,
    isTemplate as isTemplatePageResponse,
    Template,
    UpdateTemplateInput,
} from 'domains/content/types';
import BeeEditor from 'domains/content/components/Bee/BeeEditor';
import BeeEditorTestSendDialog from 'domains/content/components/Bee/BeeEditorTestSendDialog';
import useBeeAutoSave from 'domains/content/components/Bee/useBeeAutoSave';
import CopyGeneratorChat, { SaraCircleMark } from 'domains/content/components/CopyGeneratorChat';
import { getContentCreatePath } from 'pages/ContentPage/ContentTable/utils';
import useFeatureSettings from 'hooks/queries/useFeatureSettings';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useAccessCentrePermissions } from 'hooks/useAccessCentrePermissions';
import ContentStatusText, { ContentStatus } from 'pages/ContentPage/ContentTable/ContentStatusText';
import downloadFile from 'helpers/downloadFile/downloadFile';

const buttonCopy = {
    color: theme.palette.warning.content,
    backgroundColor: 'theme.palette.warning.background',
    marginLeft: theme.spacing(1),
};

const getNameHelperText = (isNameUndefinedOnTouch: boolean, isNameTooShort: boolean) => {
    if (isNameUndefinedOnTouch) {
        return 'Please enter an email name';
    }

    if (isNameTooShort) {
        return 'Email name must have at least 3 characters';
    }
};

const ContentEmailBuilderPage = () => {
    const [name, setName] = useState('');
    const [isNameTouched, setIsNameTouched] = useState(false);
    const [isTestSendDialogOpen, setIsTestSendDialogOpen] = useState(false);
    const [isCopyGeneratorChatOpen, setIsCopyGeneratorChatOpen] = useState(false);
    /**
     * Bee-specific states, hooks, and functions
     */
    const [beeJson, setBeeJson] = useState('');
    const [isNoRestart, setIsNoRestart] = useState(false);
    const [isSaveNameClicked, setIsSaveNameClicked] = useState(false);
    const [isSaveBeeJsonClicked, setIsSaveBeeJsonClicked] = useState(false);
    const [communicationType, setCommunicationType] = useState(CommunicationType.COMMERCIAL);
    const [applicationsAccess, setApplicationsAccess] = useState([ApplicationAccessType.OUTREACH]);
    const [status, setStatus] = useState(ContentStatus.DRAFT);

    const history = useHistory();

    const query = useQueryParams();
    const queryClient = useQueryClient();
    const id = query.get('id');
    const parentFolderId = Number(query.get('parentFolderId'));
    // Status from URL is prioritized over API response to handle race condition when converting
    // published content to draft - the API cache might return stale "published" status before refetch completes
    const statusFromUrlParam = query.get('status');
    // Normalize status to lowercase for consistent comparison
    const statusFromUrl = statusFromUrlParam ? (statusFromUrlParam.toLowerCase() as ContentStatus) : null;

    const onSuccess = (data: any) => {
        queryClient.invalidateQueries({
            // When we update a template, the cached network calls will be stale for the updated templates, so we need to invalidate the cached GET requests.
            predicate: (query: Query) => query.queryKey[0] === 'template-id',
        });

        // Reset to prevent useBeeAutoSave from running onUpdate() on every name change
        setIsSaveNameClicked(false);

        // If "Save & Exit" was clicked, always redirect to content listing page
        if (isSaveBeeJsonClicked) {
            setIsSaveBeeJsonClicked(false); // Reset the flag
            const contentPath = parentFolderId ? `${URLPaths.CONTENT_FOLDER}/${parentFolderId}` : URLPaths.CONTENT;
            history.push(contentPath);
            return;
        }

        // If creating or copying a template (and Save & Exit was not clicked), change the route to transition to "edit" mode.
        if (isCreate || isCopy) {
            setIsNoRestart(true);
            history.push(
                getContentCreatePath({ contentType: ContentTypeForComparison.EMAIL, id: data.id, parentFolderId })
            );
        }
    };

    const {
        error: saveError,
        isError: isSaveError,
        isLoading: isCreateLoading,
        mutate: saveTemplate,
    } = useCreateTemplate({
        onSuccess,
    });

    const {
        error: updateError,
        isError: isUpdateError,
        isLoading: isUpdateLoading,
        mutate: updateTemplate,
    } = useUpdateTemplate(id, {
        onSuccess,
    });

    const {
        data: templateQuery,
        error: templateError,
        isError: isTemplateError,
        isSuccess: isTemplateSuccess,
    } = useTemplate({ templateId: id });

    const isTemplate = isTemplateSuccess && isTemplatePageResponse(templateQuery);

    const templateBeeJson = isTemplate ? JSON.parse(templateQuery.beeJson) : templateQuery;

    // If the isCopy flag is set to true, the content create page saves a new template
    const isCopy = query.get('isCopy') === 'true';
    const isCreate = !isCopy && !isTemplate;
    const isUpdate = !isCopy && isTemplate;
    const isLayout = !isCopy && isTemplate && templateQuery.isLayout;
    const isError = isTemplateError || isSaveError || isUpdateError;

    const isSaveClicked = isSaveNameClicked || isSaveBeeJsonClicked;
    const isNameUndefinedOnSave = !name && isSaveClicked;
    const isNameUndefinedOnTouch = !name && isNameTouched;
    const isNameTooShort = isNameTouched && name.length < 3;
    const isSaving = isCreateLoading || isUpdateLoading;

    const { aiBrandTools: hasAiBrandTools, contentDraft: hasContentDraft } = useFlags();
    const { canManageContent } = useAccessCentrePermissions();

    const [isExportButtonPressed, setIsExportButtonPressed] = useState(false);
    const [isExportSuccess, setIsExportSuccess] = useState(false);
    const [exportError, setExportError] = useState<Error | null>(null);
    const [isExporting, setIsExporting] = useState(false);

    const { refetch: refetchHTML } = useTemplateHTML({
        templateId: Number(id),
        reactQueryOptions: {
            enabled: isExportButtonPressed && !!id && id !== 'default',
            staleTime: 0,
            onSuccess: (html: string) => {
                downloadFile({
                    file: html,
                    fileType: 'html',
                    fileName: name,
                });
                setIsExportSuccess(true);
                setIsExporting(false);
                setIsExportButtonPressed(false);
            },
            onError: (err: Error) => {
                setExportError(err);
                setIsExporting(false);
                setIsExportButtonPressed(false);
            },
        },
    });

    const isExportLoading = isExporting;

    const handleExportClick = () => {
        setIsExporting(true);
        setExportError(null);
        if (isExportButtonPressed) {
            refetchHTML();
        } else {
            setIsExportButtonPressed(true);
        }
    };

    const { trackChange } = useBeeAutoSave({
        applicationsAccess,
        beeJson,
        communicationType,
        content: templateQuery,
        id,
        isNameTooShort,
        isSaveClicked,
        isSaveEnabled: canManageContent,
        isTemplate: true,
        isTemplateLayout: isLayout,
        isUpdate,
        name,
        onSave: (input) => saveTemplate(input as CreateTemplateInput),
        onUpdate: (input) => updateTemplate(input as UpdateTemplateInput),
        parentFolderId,
        status,
    });

    const { data: featureSettings, isSuccess: isFeatureSettingsSuccess } = useFeatureSettings();
    const hasDynamicContent = isFeatureSettingsSuccess && featureSettings?.dynamic_content_enabled;
    const { data: beeConfig, isSuccess: isBeeConfigSuccess } = useBeeConfig(BeeConfigType.EMAIL);
    const { data: rowDisplayConditions, isSuccess: isRowDisplayConditionsSuccess } = useRowDisplayConditions({
        enabled: !!hasDynamicContent,
    });

    // Extract display condition IDs from beeJson
    const displayConditionIds = extractDisplayConditions(templateBeeJson);

    // Filter rowDisplayConditions to only include conditions that are actually used in the template
    const usedDisplayConditions =
        isRowDisplayConditionsSuccess && rowDisplayConditions
            ? rowDisplayConditions.filter((condition) => displayConditionIds.includes(condition.id))
            : [];

    // Prepare BeeEditor props for rowDisplayConditions
    const rowDisplayConditionsProps =
        isRowDisplayConditionsSuccess && rowDisplayConditions && rowDisplayConditions.length > 0
            ? { rowDisplayConditions }
            : {};

    // Update the template name local state when the template loads.
    useEffect(() => {
        if (isTemplate && isCopy) {
            setName(`Copy of ${templateQuery.name}`);
            setStatus(ContentStatus.DRAFT); // Copies are always drafts
        } else if (isTemplate && !isCopy) {
            setName(templateQuery.name);
            setCommunicationType(templateQuery.communicationType as CommunicationType);
            setApplicationsAccess(templateQuery.applicationsAccess?.split(',') as ApplicationAccessType[]);
            // Prioritize URL status (set when converting published to draft) over API response
            // This handles the race condition where cached data might be stale
            setStatus(statusFromUrl || templateQuery.status || ContentStatus.DRAFT);
        } else if (!isNoRestart) {
            setName('Untitled');
            setStatus(ContentStatus.DRAFT); // New templates are always drafts
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isTemplateSuccess]);

    /**
     * Handles the autosave event. We are not using Bee's autosave callback because the only variable we have access to is the json
     * and we need to check to see if there are unsaved changes and make the post with the name, and those variables are stale when the callback is invoked.
     */
    const handleAutoSave = (beeJson: string) => {
        setBeeJson(beeJson);
    };

    /**
     * Handles BeeJSON changes from communication type changes (removing unsubscribe links)
     * This updates the visual editor immediately but does NOT save - saving only happens on "Save & Exit"
     */
    const handleBeeChangeFromCommunicationType = (beeJson: string, beeEditorRef: any) => {
        setBeeJson(beeJson);

        // Directly update the Bee Editor content without restarting
        if (beeEditorRef && beeEditorRef.current) {
            beeEditorRef.current.load(JSON.parse(beeJson));
        }
    };

    const handleSaveBeeJson = (beeJson: string) => {
        setBeeJson(beeJson);
        setIsSaveBeeJsonClicked(true);
    };

    const handleStatusChange = (newStatus: ContentStatus) => {
        setStatus(newStatus);
    };

    const handleChangeBeeEditor = (beeJson: string, response: object) => {
        setBeeJson(beeJson);
        trackChange(response);
    };

    const handleChangeName = (e: any) => {
        if (!isNameTouched) {
            setIsNameTouched(true);
        }
        setName(e.target.value);
    };

    const handleCommunicationTypeChange = (e: any) => {
        setCommunicationType(e.target.value);
    };

    const handleApplicationsAccessChange = (e: any) => {
        setApplicationsAccess(e.target.value);
    };

    const handleSaveName = () => {
        const getJson = () => {
            if (beeJson) {
                return beeJson;
            }
            if (isTemplate) {
                return templateQuery.beeJson;
            }
            return JSON.stringify(templateQuery);
        };
        setBeeJson(getJson());
        setIsSaveNameClicked(true);
    };

    const handleClickCopy = () => {
        setName(`Copy of ${name}`);
        // Pass the isCopy flag. If the flag isn't passed, the value for isCopy defaults to false.
        history.push(`${URLPaths.CONTENT_CREATE_EMAIL}?id=${id}&isCopy=true`);
    };

    const handleCloseTestSendDialog = () => setIsTestSendDialogOpen(false);

    return (
        <Stack height="100%" mb={4} rowGap={4}>
            <Box sx={{ display: 'inline-flex', alignItems: 'flex-start', gap: 2 }}>
                <ContentNameInput
                    dataTestId="name-input"
                    disabled={isLayout}
                    error={isNameUndefinedOnTouch || isNameTooShort}
                    helperText={getNameHelperText(isNameUndefinedOnTouch, isNameTooShort)}
                    name={name}
                    withClickAway
                    onChange={handleChangeName}
                    onClickAway={handleSaveName}
                />
                {hasContentDraft && (
                    <Box sx={{ pt: 3 }}>
                        <ContentStatusText
                            status={
                                id && id !== 'default' && !isCopy
                                    ? status || (templateQuery as any)?.status || ' '
                                    : ContentStatus.DRAFT
                            }
                        />
                    </Box>
                )}
            </Box>

            <BeeEditorTestSendDialog
                handleClose={handleCloseTestSendDialog}
                isOpen={isTestSendDialogOpen}
                templateId={Number(id)}
                displayConditions={usedDisplayConditions}
                hasDynamicContent={!!hasDynamicContent}
            />
            {(isError || isNameUndefinedOnSave) && (
                <ErrorSnackbar
                    errorMessage={
                        templateError?.message ||
                        saveError?.message ||
                        updateError?.message ||
                        'Your email must include a name.'
                    }
                />
            )}
            <EmailExportSnackbar
                isDownloading={isExportLoading}
                isSuccess={isExportSuccess}
                error={exportError}
                emailName={name}
                onSuccessClosed={() => setIsExportSuccess(false)}
                onErrorClosed={() => setExportError(null)}
            />
            {isLayout && (
                <Box display="flex" flex={1} mb={1} justifyContent="center">
                    <Alert
                        elevation={0}
                        severity="warning"
                        variant="standard"
                        action={
                            <Button data-testid="copy-button" sx={buttonCopy} onClick={handleClickCopy}>
                                Copy
                            </Button>
                        }
                    >
                        Edits to this email will not be saved. Please make a copy of this email to save changes.
                    </Alert>
                </Box>
            )}
            <Stack height="100%">
                {isBeeConfigSuccess && (
                    <BeeEditor
                        applicationsAccess={applicationsAccess}
                        beeConfig={beeConfig}
                        beeJson={templateBeeJson}
                        communicationType={communicationType}
                        handleApplicationsAccess={handleApplicationsAccessChange}
                        handleCommunicationType={handleCommunicationTypeChange}
                        isNoRestart={isNoRestart}
                        isSuccess={isTemplateSuccess}
                        isSaving={isSaving}
                        isExportLoading={isExportLoading}
                        onExportClick={handleExportClick}
                        onAutoSave={handleAutoSave}
                        onChange={handleChangeBeeEditor}
                        onSave={handleSaveBeeJson}
                        onStatusChange={handleStatusChange}
                        openSendTestDialog={() => setIsTestSendDialogOpen(true)}
                        setIsNoRestart={setIsNoRestart}
                        template={templateQuery as Template}
                        hasDynamicContent={!!hasDynamicContent}
                        hasUsedDisplayConditions={usedDisplayConditions.length > 0}
                        onBeeChangeFromCommunicationType={handleBeeChangeFromCommunicationType}
                        {...rowDisplayConditionsProps}
                    />
                )}
            </Stack>
            {!!hasAiBrandTools && (
                <>
                    <CopyGeneratorChat
                        open={isCopyGeneratorChatOpen}
                        onClose={() => setIsCopyGeneratorChatOpen(false)}
                    />
                    <Tooltip placement="right" title="Ask Sara">
                        <Fab
                            aria-expanded={isCopyGeneratorChatOpen}
                            aria-haspopup="dialog"
                            aria-label="Ask Sara"
                            data-testid="copy-generator-chat-fab"
                            size="medium"
                            sx={(theme) => ({
                                position: 'fixed',
                                left: theme.spacing(9),
                                bottom: theme.spacing(3),
                                zIndex: 1300,
                                bgcolor: 'transparent',
                                backgroundImage: 'none',
                                boxShadow: theme.shadows[6],
                                '&:hover': {
                                    bgcolor: 'transparent',
                                    backgroundImage: 'none',
                                    filter: 'brightness(1.06)',
                                },
                                ...(isCopyGeneratorChatOpen && {
                                    boxShadow: `0 0 0 3px ${theme.palette.common.white}, ${theme.shadows[8]}`,
                                }),
                            })}
                            onClick={() => setIsCopyGeneratorChatOpen((open) => !open)}
                        >
                            <SaraCircleMark size={56} />
                        </Fab>
                    </Tooltip>
                </>
            )}
        </Stack>
    );
};

export default ContentEmailBuilderPage;
